import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { post } from "../api";
import { Badge, Button } from "../ui";
import { useInteractiveFormValidation, validateFormFields } from "../form-validation";
import type { ValidationField } from "../form-validation";
import { FieldError, ValidationSummary } from "./ValidationSummary";

export type FirmSignupValues = {
  firmName: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  syntheticOnly: boolean;
};
const emptyValues = (): FirmSignupValues => ({
  firmName: "",
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  syntheticOnly: false,
});

export function isFirmSignupEnabled(registration: unknown): boolean {
  if (!registration || typeof registration !== "object") return false;
  const value = registration as Record<string, unknown>;
  return (
    value.enabled === true &&
    value.syntheticOnly === true &&
    value.mfaRequired === true
  );
}

export function legacyFirmSignupUrl(
  mode: unknown,
  origin: string,
): string | null {
  return mode === "LOCAL_SYNTHETIC" && origin === "http://127.0.0.1:4317"
    ? "http://localhost:4319/auth/signup"
    : null;
}

export function firmSignupFields(
  values: FirmSignupValues,
  errors: Partial<Record<keyof FirmSignupValues, string>> = {},
): ValidationField[] {
  const field = (
    key: keyof FirmSignupValues,
    label: string,
    options: Partial<ValidationField> = {},
  ): ValidationField => ({
    id: `signup-${key}`,
    label,
    value: values[key],
    required: true,
    ...options,
    validate: (value) => options.validate?.(value) || errors[key],
  });
  return [
    field("firmName", "Firm name", {
      minLength: 2,
      maxLength: 120,
      validate: (value) =>
        String(value ?? "").trim().length >= 2
          ? undefined
          : "Enter a firm name containing at least 2 characters, excluding surrounding spaces.",
    }),
    field("name", "Full name", {
      minLength: 2,
      maxLength: 100,
      validate: (value) =>
        String(value ?? "").trim().length >= 2
          ? undefined
          : "Enter your full name containing at least 2 characters, excluding surrounding spaces.",
    }),
    field("email", "Email address", {
      maxLength: 254,
      validate: (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim())
          ? undefined
          : "Enter an email address with a name, @ sign, and domain.",
    }),
    field("password", "Password", { minLength: 12, maxLength: 256 }),
    field("confirmPassword", "Confirm password", {
      maxLength: 256,
      validate: (value) =>
        value === values.password
          ? undefined
          : "Enter the same password in both password fields.",
    }),
    field("syntheticOnly", "Synthetic-data agreement", {
      value: values.syntheticOnly === true ? true : undefined,
      requiredMessage:
        "Confirm that you will use synthetic test data only. Real taxpayer information is not authorized in this workspace.",
    }),
  ];
}

export function buildFirmSignupRequest(values: FirmSignupValues) {
  if (
    validateFormFields("Create firm account", firmSignupFields(values)).length
  )
    throw new Error("Complete the firm account fields before submitting.");
  return {
    name: values.name.trim(),
    email: values.email.trim().toLowerCase(),
    password: values.password,
    firmName: values.firmName.trim(),
    syntheticOnly: true,
  };
}

export function isFirmSignupResult(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const result = value as Record<string, unknown>;
  return (
    result.registered === true &&
    result.signInRequired === true &&
    result.mfaRequired === true
  );
}

export function firmSignupFailure(error: unknown): {
  field?: keyof FirmSignupValues;
  message: string;
} {
  const code = (error as { code?: string } | null)?.code;
  switch (code) {
    case "INVALID_EMAIL":
      return {
        field: "email",
        message: "Enter an email address with a name, @ sign, and domain.",
      };
    case "WEAK_PASSWORD":
      return {
        field: "password",
        message: "Use a password containing 12–256 characters.",
      };
    case "SYNTHETIC_CONFIRMATION_REQUIRED":
      return {
        field: "syntheticOnly",
        message:
          "Confirm that this firm will use synthetic test data only. Do not enter real taxpayer information.",
      };
    case "INVALID_FIELD": {
      const detail = (error as { message?: unknown } | null)?.message;
      if (detail === "Firm name must contain 2–120 characters.")
        return {
          field: "firmName",
          message:
            "Enter a firm name containing 2–120 characters, excluding surrounding spaces.",
        };
      if (detail === "Full name must contain 2–100 characters.")
        return {
          field: "name",
          message:
            "Enter your full name containing 2–100 characters, excluding surrounding spaces.",
        };
      return {
        message:
          "Review Firm name (2–120 characters) and Full name (2–100 characters), excluding surrounding spaces. Correct those fields before trying again.",
      };
    }
    case "ACCOUNT_EXISTS":
    case "DUPLICATE_RECORD":
    case "REGISTRATION_FAILED":
    case "REGISTRATION_UNAVAILABLE":
      return {
        message:
          "Unable to create an account with these details. Sign in if you already have an account; otherwise ask the workspace administrator for help.",
      };
    case "REGISTRATION_DISABLED":
      return {
        message:
          "Firm account creation is not available in this workspace. Sign in to an existing account or contact the workspace administrator.",
      };
    case "SIGN_OUT_REQUIRED":
      return {
        message:
          "You are already signed in. Sign out of that account before creating a separate firm account.",
      };
    case "RATE_LIMITED":
    case "RATE_LIMIT_CAPACITY":
      return {
        message:
          "Too many account-creation attempts. Wait before trying again; do not keep submitting the form.",
      };
    default:
      return {
        message:
          "We could not confirm whether your firm account was created. Check your connection, then try signing in with these details before submitting again. No email has been sent.",
      };
  }
}

export function FirmSignup({
  registration,
  onSignIn,
}: {
  registration: unknown;
  onSignIn: (email?: string) => void;
}) {
  const [values, setValues] = useState(emptyValues);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FirmSignupValues, string>>
  >({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const submitting = useRef(false);
  const mounted = useRef(true);
  const enabled = isFirmSignupEnabled(registration);
  const validation = useInteractiveFormValidation(
    "Create firm account",
    enabled && !registeredEmail ? firmSignupFields(values, fieldErrors) : [],
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function edit<K extends keyof FirmSignupValues>(
    key: K,
    value: FirmSignupValues[K],
  ) {
    setValues((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[key];
      if (key === "password") delete next.confirmPassword;
      return next;
    });
    setError("");
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      submitting.current ||
      !enabled ||
      registeredEmail ||
      !validation.validate()
    )
      return;
    const request = buildFirmSignupRequest(values);
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await post("/auth/register", request);
      if (!mounted.current) return;
      if (!isFirmSignupResult(result))
        throw new Error("Registration confirmation unavailable");
      setRegisteredEmail(request.email);
      setValues(emptyValues());
      setFieldErrors({});
    } catch (failure) {
      if (!mounted.current) return;
      const problem = firmSignupFailure(failure);
      if (problem.field) setFieldErrors({ [problem.field]: problem.message });
      else setError(problem.message);
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  if (registeredEmail)
    return (
      <>
        <Badge>Account created</Badge>
        <h2>Your firm account is ready.</h2>
        <p>
          Sign in with <strong>{registeredEmail}</strong>, then set up this
          account's authenticator before accessing protected workspace features.
        </p>
        <p>
          Creating an account does not sign you in. No invitation or
          verification email was sent, and your email address has not been
          verified.
        </p>
        <Button
          variant="primary full"
          onClick={() => onSignIn(registeredEmail)}
        >
          Continue to sign in
        </Button>
        <p className="auth-disclaimer">
          Synthetic test data only. This account does not authorize real
          taxpayer data, tax filing, or production use.
        </p>
      </>
    );
  if (!enabled)
    return (
      <>
        <Badge>Local workspace</Badge>
        <h2>Account creation is unavailable.</h2>
        <p>
          This workspace is not accepting new firm accounts. Sign in to an
          existing account or contact the workspace administrator.
        </p>
        <Button onClick={() => onSignIn()}>Back to sign in</Button>
      </>
    );
  return (
    <>
      <Badge>Synthetic workspace</Badge>
      <h2>Create a firm account.</h2>
      <p>
        Create a separate firm workspace and your administrator account. You
        will sign in and set up an authenticator next.
      </p>
      <form onSubmit={submit} noValidate aria-busy={busy}>
        <ValidationSummary issues={validation.issues} />
        {(
          [
            ["firmName", "Firm name", "text", "organization", 120],
            ["name", "Full name", "text", "name", 100],
            ["email", "Email address", "email", "username", 254],
            ["password", "Password", "password", "new-password", 256],
            [
              "confirmPassword",
              "Confirm password",
              "password",
              "new-password",
              256,
            ],
          ] as const
        ).map(([key, label, type, autoComplete, maxLength]) => (
          <label key={key} htmlFor={`signup-${key}`}>
            {label}
            <input
              {...validation.fieldProps(`signup-${key}`)}
              name={key}
              type={type}
              autoComplete={autoComplete}
              maxLength={maxLength}
              minLength={key === "password" ? 12 : undefined}
              value={values[key]}
              onChange={(event) => edit(key, event.target.value)}
              disabled={busy}
              required
            />
            <FieldError issue={validation.issue(`signup-${key}`)} />
          </label>
        ))}
        <p>
          Use at least 12 characters for your password. Keep it private; do not
          put it in notes or messages.
        </p>
        <label className="checkbox-label" htmlFor="signup-syntheticOnly">
          <input
            {...validation.fieldProps("signup-syntheticOnly")}
            type="checkbox"
            name="syntheticOnly"
            checked={values.syntheticOnly}
            onChange={(event) => edit("syntheticOnly", event.target.checked)}
            disabled={busy}
            required
          />
          I will use synthetic test data only. I will not enter real taxpayer
          information.
        </label>
        <FieldError issue={validation.issue("signup-syntheticOnly")} />
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary full" disabled={busy}>
          {busy ? "Creating your account…" : "Create firm account"}
        </Button>
      </form>
      <button
        type="button"
        className="text-button"
        disabled={busy}
        onClick={() => onSignIn(values.email.trim())}
      >
        Already have an account? Sign in
      </button>
      <p className="auth-disclaimer">
        Synthetic data only. No email delivery, tax filing, or production
        certification is enabled by signing up.
      </p>
    </>
  );
}
